import React, { useEffect, useRef } from 'react';
import { ArrowDown, Volume2, VolumeX } from 'lucide-react';

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import SplitType from 'split-type';
import Particles from 'react-tsparticles';
import { useTranslation } from 'react-i18next';

gsap.registerPlugin(ScrollTrigger);

export default function Hero({
  onExploreClick,
  onSeekRetreatClick,
  audioPlaying,
  onToggleAudio
}) {
  const { t } = useTranslation();
  const heroRef = useRef(null);

  const bgRef = useRef(null);

  const titleRef = useRef(null);

  const subtitleRef = useRef(null);

  const buttonsRef = useRef(null);

  useEffect(() => {

    // =========================
    // SPLIT TEXT
    // =========================

    const split = new SplitType(titleRef.current, {
      types: 'words,chars'
    });

    // Keep each word as an inline-block so lines never break mid-word
    if (split.words) {
      split.words.forEach(word => {
        word.style.display = 'inline-block';
      });
    }

    // =========================
    // ENTRY TIMELINE
    // =========================

    const tl = gsap.timeline();

    tl.from(
      split.chars,
      {
        opacity: 0,
        y: 120,
        rotateX: -90,
        stagger: 0.03,
        duration: 1.5,
        ease: 'power4.out'
      },
      '-=0.7'
    );

    tl.from(
      subtitleRef.current,
      {
        opacity: 0,
        y: 50,
        duration: 1.2,
        ease: 'power3.out'
      },
      '-=1'
    );

    tl.from(
      buttonsRef.current,
      {
        opacity: 0,
        y: 30,
        duration: 1
      },
      '-=0.8'
    );

    // =========================
    // CINEMATIC SCROLL
    // =========================

    gsap.to(bgRef.current, {
      scale: 1.25,
      y: 120,
      rotate: 1,
      ease: 'none',

      scrollTrigger: {
        trigger: heroRef.current,
        start: 'top top',
        end: 'bottom top',
        scrub: 1.5
      }
    });

    gsap.to(titleRef.current, {

      y: -150,
      opacity: 0,

      scrollTrigger: {
        trigger: heroRef.current,
        start: 'top top',
        end: 'bottom top',
        scrub: 1
      }
    });

    gsap.to(subtitleRef.current, {

      y: -100,
      opacity: 0,

      scrollTrigger: {
        trigger: heroRef.current,
        start: 'top top',
        end: 'bottom top',
        scrub: 1
      }
    });

    gsap.to(buttonsRef.current, {

      y: -70,
      opacity: 0,

      scrollTrigger: {
        trigger: heroRef.current,
        start: 'top top',
        end: 'bottom top',
        scrub: 1
      }
    });

    return () => {

      split.revert();

      ScrollTrigger.getAll().forEach((trigger) =>
        trigger.kill()
      );

    };

  }, []);

  // =========================
  // VIDEO AUDIO CONTROL
  // =========================

  useEffect(() => {

    if (bgRef.current) {

      bgRef.current.muted = !audioPlaying;

      // Keep the background video playing and only toggle its muted property
      bgRef.current.play().catch(() => { });

    }

  }, [audioPlaying]);

  return (

    <section
      ref={heroRef}
      id="hero"
      style={{
        height: '100vh',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        background: '#ffffffff'
      }}
    >

      {/* ========================= */}
      {/* VIDEO BACKGROUND */}
      {/* ========================= */}

      <video
        ref={bgRef}
        autoPlay
        loop
        playsInline
        muted
        controls={false}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 1,
          transform: 'scale(1.05)'
        }}
      >
        <source
          src="/videos/spiritual.mp4"
          type="video/mp4"
        />
      </video>

      {/* ========================= */}
      {/* DARK OVERLAY */}
      {/* ========================= */}

      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.65) 50%, rgba(0,0,0,0.85) 100%)',
          zIndex: 2
        }}
      />

      {/* ========================= */}
      {/* GOLD GLOW */}
      {/* ========================= */}

      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at center, rgba(214,179,106,0.18), transparent 70%)',
          zIndex: 3
        }}
      />

      {/* ========================= */}
      {/* WHITE BOTTOM FADE */}
      {/* ========================= */}

      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '100px',
          background: 'linear-gradient(to bottom, transparent, #000000ff)',
          zIndex: 6,
          pointerEvents: 'none'

        }}
      />

      {/* ========================= */}
      {/* PARTICLES */}
      {/* ========================= */}

      <Particles
        options={{
          fullScreen: false,

          particles: {

            number: {
              value: 45
            },

            color: {
              value: '#d6b36a'
            },

            opacity: {
              value: 0.25
            },

            size: {
              value: 2
            },

            move: {
              enable: true,
              speed: 0.5
            }
          }
        }}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 4
        }}
      />



      {/* ========================= */}
      {/* CONTENT */}
      {/* ========================= */}

      <div
        style={{
          position: 'relative',
          zIndex: 10,
          width: '100%',
          maxWidth: '950px',
          padding: '0 20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center'
        }}
      >



        {/* TITLE */}

        <h1
          ref={titleRef}
          style={{
            fontSize: 'clamp(3rem, 7vw, 6rem)',
            lineHeight: 1.05,
            color: 'white',
            fontWeight: 200,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            margin: 0,
            textAlign: 'center',
            width: '100%',
            textShadow: '0 2px 40px rgba(0, 0, 0, 0.9), 0 0 80px rgba(0,0,0,0.6)'
          }}
        >
          {t('hero.title')}

          <span
            style={{
              display: 'block',
              marginTop: '1.5rem',
              color: '#fce7ba',
              fontWeight: 300,
              fontSize: '18px',
              textShadow: '0 2px 20px rgba(0,0,0,0.8)',
              maxWidth: '800px'
            }}
          >
            {t('hero.subtitle')}
          </span>
        </h1>

        {/* SUBTITLE */}




      </div>

      {/* ========================= */}
      {/* SCROLL INDICATOR */}
      {/* ========================= */}

      <div
        onClick={onExploreClick}
        style={{
          position: 'absolute',
          bottom: '40px',
          zIndex: 10,
          color: '#d6b36a',
          cursor: 'pointer',
          animation: 'float 2s infinite'
        }}
      >
        <ArrowDown size={24} />
      </div>

      {/* ========================= */}
      {/* AUDIO TOGGLE BUTTON */}
      {/* ========================= */}
      
      <button
        onClick={onToggleAudio}
        className="hero-audio-btn"
        style={{
          position: 'fixed',
          zIndex: 999,
          top: '90px',
          left: '40px',
          background: 'rgba(10, 8, 6, 0.5)',
          border: '1px solid rgba(212, 175, 55, 0.4)',
          borderRadius: '50%',
          width: '60px',
          height: '60px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--primary-gold)',
          cursor: 'pointer',
          backdropFilter: 'blur(5px)',
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212, 175, 55, 0.15)'; e.currentTarget.style.borderColor = 'var(--primary-gold)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(10, 8, 6, 0.5)'; e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.4)'; }}
        title={audioPlaying ? t('header.btnAudioMute', {defaultValue: 'Mute'}) : t('header.btnAudioPlay', {defaultValue: 'Play'})}
      >
        {audioPlaying ? <Volume2 size={24} /> : <VolumeX size={24} />}
      </button>

      {/* ========================= */}
      {/* STYLES */}
      {/* ========================= */}

      <style>{`

        @media (max-width: 768px) {
          .hero-audio-btn { top: 90px !important; bottom: auto !important; left: 16px !important; width: 50px !important; height: 50px !important; }
        }

        @keyframes float {

          0% {
            transform: translateY(0px);
          }

          50% {
            transform: translateY(10px);
          }

          100% {
            transform: translateY(0px);
          }

        }

      `}</style>

    </section>

  );

}